/**
 * Status History — read-only journey of an application across statuses.
 *
 * Use case: when an application moves from one status to the next (e.g.
 * Formalities Check Fail → Marked for Exam), the data captured during the
 * earlier status is preserved in its dedicated stage table but is no longer
 * visible from the current-status view. This screen surfaces that history.
 *
 * Layout follows the same primitives as MarkedForExamView / FormalitiesCheckView:
 * - TM No. selector chips at the top (one chip per application under the lead).
 * - Vertical timeline of status segments (newest at top) with date entered /
 *   exited, who changed it, and the stage data captured during that segment.
 * - Each segment is expandable; expanded body renders the stage data as a
 *   read-only field list. Files have download links.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  getApplicationsByStatus,
  getStatusTimeline,
  getStatusTimelineSegment,
  type ApplicationsByStatusItem,
  type StatusTimelineFcConfirmationBlock,
  type StatusTimelineFcFailData,
  type StatusTimelineGroup,
  type StatusTimelineHearingData,
  type StatusTimelineInvoiceBlock,
  type StatusTimelineObjectionConfirmationBlock,
  type StatusTimelineObjectionData,
  type StatusTimelineAcceptedData,
  type StatusTimelineAdvertisedData,
  type StatusTimelineRegisteredData,
  type StatusTimelineRefusedAbandonedData,
  type StatusTimelineResponse,
  type StatusTimelineSegment,
  type StatusTimelineSegmentData,
  type TimelineAction,
  type TimelineActionKind,
} from "api/discussion";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";

// Groups that have a dedicated stage data table; for these we lazy-load the
// segment's data on expand. Other groups (e.g. "marked-for-exam") never have
// any captured data, so we only render the activity list for them.
const DATA_GROUPS: ReadonlySet<StatusTimelineGroup> = new Set<StatusTimelineGroup>([
  "formalities-check-fail",
  "examination-report-objected",
  "examination-report-accepted",
  "examination-report-accepted-after-objection",
  "examination-report-hearing",
  "advertised",
  "advertised-opposed",
  "advertised-not-opposed",
  "registered",
  "refused",
  "abandoned",
]);

function isDataGroup(group: StatusTimelineGroup): boolean {
  return DATA_GROUPS.has(group);
}

type SegmentDataState = {
  loading: boolean;
  loaded: boolean;
  data: StatusTimelineSegmentData;
  error: string | null;
};

// ---------------------------------------------------------------------------
// Types & props
// ---------------------------------------------------------------------------

type TmEntry = { tmNo: string; mark: string };

export type StatusHistoryViewProps = {
  leadId: string | null | undefined;
};

// ---------------------------------------------------------------------------
// Primitives (kept consistent with MarkedForExamView)
// ---------------------------------------------------------------------------

function RadioCircle({ active }: { active: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      <circle cx="8" cy="8" r="8" fill={active ? "white" : "#6D6D6D"} />
      <path
        d="M4.5 8.2 L6.8 10.5 L11.6 5.3"
        stroke={active ? "#222222" : "white"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        transition: "transform 150ms ease",
        transform: open ? "rotate(90deg)" : "rotate(0deg)",
      }}
    >
      <path
        d="M6 4 L10 8 L6 12"
        stroke="#6D6D6D"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8 2.5V10.5M8 10.5L5 7.5M8 10.5L11 7.5M3 12.5V13.5H13V12.5"
        stroke="#222222"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ActionIcon({ kind }: { kind: TimelineActionKind }) {
  // Single SVG per kind family; colors stay neutral and match design tokens.
  const stroke = "#222222";
  switch (kind) {
    case "notice-to-client":
    case "inform-client":
      // Paper-plane (message sent)
      return (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path
            d="M14 2L2 7.5L6.5 9L8.5 13L14 2Z"
            stroke={stroke}
            strokeWidth="1.3"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      );
    case "invoice":
    case "payment":
      // Rupee/currency
      return (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path
            d="M4 4H12M4 7H12M6 4C6 6 4 7 4 7L10 13"
            stroke={stroke}
            strokeWidth="1.3"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      );
    case "call-client":
      // Phone
      return (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path
            d="M3 4C3 3.5 3.4 3 4 3H5.5L6.5 5.5L5 6.5C5.5 8 7 9.5 8.5 10L9.5 8.5L12 9.5V11C12 11.6 11.5 12 11 12C7.1 12 3 7.9 3 4Z"
            stroke={stroke}
            strokeWidth="1.3"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      );
    case "documents":
    case "filing":
      // Document
      return (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path
            d="M4 2H10L13 5V14H4V2Z"
            stroke={stroke}
            strokeWidth="1.3"
            strokeLinejoin="round"
          />
          <path d="M10 2V5H13" stroke={stroke} strokeWidth="1.3" />
        </svg>
      );
    case "confirmation":
    case "save-data":
      // Checkmark in circle
      return (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="6" stroke={stroke} strokeWidth="1.3" />
          <path
            d="M5.5 8.2L7.3 10L10.5 6.5"
            stroke={stroke}
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      );
    case "skipped":
      // Forward arrow
      return (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path
            d="M3 8H12M9 5L12 8L9 11"
            stroke={stroke}
            strokeWidth="1.3"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      );
    default:
      // Generic dot
      return (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="2" fill={stroke} />
        </svg>
      );
  }
}

function ActivityList({ actions }: { actions: TimelineAction[] }) {
  if (!actions.length) return null;
  return (
    <div
      className="flex flex-col gap-2 pt-3 border-t border-[#EEEEEE]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-xs font-medium text-[#222222]"
        style={{ lineHeight: "20px" }}
      >
        Activity ({actions.length})
      </span>
      <ul className="flex flex-col gap-2">
        {actions.map((action, idx) => {
          const statusPillBg =
            action.status === "skipped"
              ? "bg-[#F3F4F6] text-[#6D6D6D]"
              : action.status === "superseded" || action.status === "reverted"
                ? "bg-[#FEF2F2] text-[#B91C1C]"
                : "bg-[#ECFDF5] text-[#15803D]";
          const statusLabel =
            action.status === "completed"
              ? "Done"
              : action.status === "skipped"
                ? "Skipped"
                : action.status === "submitted"
                  ? "Submitted"
                  : action.status === "superseded"
                    ? "Superseded"
                    : "Reverted";
          return (
            <li
              key={`${action.step}-${action.performedAt}-${idx}`}
              className="flex items-start gap-2.5 py-1.5"
            >
              <div className="flex-shrink-0 mt-0.5">
                <ActionIcon kind={action.kind} />
              </div>
              <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="text-xs font-medium text-[#222222]"
                    style={{ lineHeight: "20px" }}
                  >
                    {action.label}
                  </span>
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${statusPillBg}`}
                    style={{ lineHeight: "14px" }}
                  >
                    {statusLabel}
                  </span>
                </div>
                <div
                  className="flex flex-row flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[#6D6D6D]"
                  style={{ lineHeight: "16px" }}
                >
                  {action.performedAt && <span>{action.performedAt}</span>}
                  {action.performedBy?.name && (
                    <span>by {action.performedBy.name}</span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ApplicationSummary({
  app,
  acknowledgementNo,
  dateOfFiling,
}: {
  app: StatusTimelineResponse["application"];
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
}) {
  const items: { label: string; value: string }[] = [
    { label: "Mark", value: app.mark || "—" },
    { label: "Class", value: app.classNumber ? String(app.classNumber) : "—" },
    { label: "Type", value: app.typeOfMark || "—" },
    {
      label: "Application Date",
      value: app.applicationDate || dateOfFiling || "—",
    },
    {
      label: "Acknowledgement No.",
      value: acknowledgementNo || "—",
    },
    {
      label: "Current Status",
      value: app.currentStatus || "—",
    },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4 rounded-xl border border-[#EEEEEE] bg-[#FCFCFC]">
      {items.map((item) => (
        <div key={item.label} className="flex flex-col gap-1">
          <span
            className="text-[11px] font-normal text-[#6D6D6D]"
            style={{ lineHeight: "16px" }}
          >
            {item.label}
          </span>
          <span
            className="text-xs font-medium text-[#222222] break-words"
            style={{ lineHeight: "20px" }}
          >
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field list — one row per label/value for read-only stage data
// ---------------------------------------------------------------------------

type FieldRow = {
  label: string;
  value: string;
};

function FieldList({
  fields,
  files,
}: {
  fields: FieldRow[];
  files?: Array<{ label: string; filename: string | null; url: string | null }>;
}) {
  const renderableFiles = (files ?? []).filter(
    (f) => f.filename || f.url,
  );
  if (!fields.length && !renderableFiles.length) {
    return (
      <p
        className="text-xs text-[#6D6D6D]"
        style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
      >
        No details were captured for this status.
      </p>
    );
  }
  return (
    <div
      className="flex flex-col gap-3"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {fields.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
          {fields.map((row) => (
            <div key={row.label} className="flex flex-col gap-0.5">
              <span
                className="text-[11px] font-normal text-[#6D6D6D]"
                style={{ lineHeight: "16px" }}
              >
                {row.label}
              </span>
              <span
                className="text-xs font-medium text-[#222222] break-words"
                style={{ lineHeight: "20px" }}
              >
                {row.value || "—"}
              </span>
            </div>
          ))}
        </div>
      )}
      {renderableFiles.length > 0 && (
        <div className="flex flex-col gap-2">
          {renderableFiles.map((f) => (
            <div key={f.label} className="flex flex-col gap-1">
              <span
                className="text-[11px] font-normal text-[#6D6D6D]"
                style={{ lineHeight: "16px" }}
              >
                {f.label}
              </span>
              {f.url ? (
                <a
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-[6px] border border-[#EEEEEE] bg-white text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors w-fit max-w-full"
                  style={{ lineHeight: "20px" }}
                >
                  <DownloadIcon />
                  <span className="truncate">
                    {f.filename || "Download"}
                  </span>
                </a>
              ) : (
                <span
                  className="text-xs text-[#6D6D6D]"
                  style={{ lineHeight: "20px" }}
                >
                  {f.filename || "—"}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data renderers — convert each group's payload into FieldList rows
// ---------------------------------------------------------------------------

function formatINR(amount: number | null | undefined): string {
  if (amount == null || Number.isNaN(amount)) return "—";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `₹${amount}`;
  }
}

function SubSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 pt-3 border-t border-[#EEEEEE]">
      <span
        className="text-xs font-medium text-[#222222]"
        style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
      >
        {title}
      </span>
      {children}
    </div>
  );
}

function renderInvoice(invoice: StatusTimelineInvoiceBlock, invoiceLabel = "Invoice PDF") {
  const fields: FieldRow[] = [
    { label: "Invoice No.", value: invoice.invoiceNo ?? "" },
    { label: "Invoice Date", value: invoice.invoiceDate ?? "" },
    {
      label: "Invoice Amount",
      value: formatINR(invoice.invoiceAmount ?? invoice.computedInvoiceTotal ?? null),
    },
    invoice.discount != null && invoice.discount > 0
      ? { label: "Discount", value: formatINR(invoice.discount) }
      : null,
    {
      label: "Collected Amount",
      value: formatINR(invoice.collectedAmount),
    },
    {
      label: "Outstanding Amount",
      value: formatINR(invoice.outstandingAmount),
    },
  ].filter(Boolean) as FieldRow[];
  return (
    <FieldList
      fields={fields}
      files={[
        {
          label: invoiceLabel,
          filename: invoice.invoicePdfKey ? invoiceLabel : null,
          url: invoice.invoicePdfUrl,
        },
      ]}
    />
  );
}

function renderFcConfirmation(c: StatusTimelineFcConfirmationBlock) {
  const yesNo =
    c.clientGoingAhead === true ? "Yes" : c.clientGoingAhead === false ? "No" : "—";
  return (
    <FieldList
      fields={[
        { label: "Client Going Ahead", value: yesNo },
        { label: "Invoice Amount", value: formatINR(c.invoiceAmount) },
        { label: "Collected Amount", value: formatINR(c.collectedAmount) },
        { label: "Outstanding Amount", value: formatINR(c.outstandingAmount) },
      ]}
    />
  );
}

function renderObjectionConfirmation(c: StatusTimelineObjectionConfirmationBlock) {
  const yesNo =
    c.clientGoingAhead === true ? "Yes" : c.clientGoingAhead === false ? "No" : "—";
  return <FieldList fields={[{ label: "Client Going Ahead", value: yesNo }]} />;
}

function renderFcFail(data: StatusTimelineFcFailData) {
  const fields: FieldRow[] = [
    { label: "Date of FC Notice", value: data.dateOfFcNotice ?? "" },
    { label: "Reason for FC Failure", value: data.reasonForFcFailure ?? "" },
    { label: "Last Date for FC Reply", value: data.lastDateForFcReply ?? "" },
  ];
  const files = [
    {
      label: "Notice of FC Fail",
      filename: data.noticeOfFcFailFilename,
      url: data.noticeOfFcFailFileUrl,
    },
  ];
  return (
    <div className="flex flex-col gap-4">
      <FieldList fields={fields} files={files} />
      {data.invoice && (
        <SubSection title="Invoice to Client (FC Reply)">
          {renderInvoice(data.invoice, "FC Reply Invoice PDF")}
        </SubSection>
      )}
      {data.confirmation && (
        <SubSection title="Confirmation from Client">
          {renderFcConfirmation(data.confirmation)}
        </SubSection>
      )}
      {data.replyFiled && (
        <SubSection title="Reply Filed">
          <FieldList
            fields={[
              {
                label: "Date of FC Reply Filed",
                value: data.replyFiled.dateOfFcReplyFiled ?? "",
              },
            ]}
            files={[
              {
                label: "FC Reply",
                filename: data.replyFiled.fcReplyFilename,
                url: data.replyFiled.fcReplyFileUrl,
              },
            ]}
          />
        </SubSection>
      )}
    </div>
  );
}

function renderObjectionReasons(
  reasons: StatusTimelineObjectionData["reasonForObjection"],
): string {
  const parts: string[] = [];
  if (reasons.similarity) parts.push("Similarity");
  if (reasons.nonDistinctive) parts.push("Non Distinctive");
  if (reasons.supportingDocuments) parts.push("Supporting Documents");
  if (reasons.tmMYes) parts.push("TM-M : Yes");
  if (reasons.others) {
    const specify = reasons.othersSpecify?.trim();
    parts.push(specify ? `Others (${specify})` : "Others");
  }
  return parts.join(", ");
}

function renderObjection(data: StatusTimelineObjectionData) {
  const fields: FieldRow[] = [
    {
      label: "Date of Examination Report",
      value: data.dateOfExaminationReport ?? "",
    },
    {
      label: "Reason for Objection",
      value: renderObjectionReasons(data.reasonForObjection) || "",
    },
    {
      label: "Last Date for Reply to Objection",
      value: data.lastDateForReplyToObjection ?? "",
    },
  ];
  const files = [
    {
      label: "Examination Report",
      filename: data.examinationReportFilename,
      url: data.examinationReportFileUrl,
    },
  ];
  return (
    <div className="flex flex-col gap-4">
      <FieldList fields={fields} files={files} />
      {data.similarMarks.length > 0 && (
        <SubSection title={`Similar Marks (${data.similarMarks.length})`}>
          <div className="overflow-x-auto">
            <table
              className="min-w-full text-xs"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <thead>
                <tr className="text-left text-[#6D6D6D]">
                  <th className="font-normal px-2 py-1.5">Mark</th>
                  <th className="font-normal px-2 py-1.5">TM No.</th>
                  <th className="font-normal px-2 py-1.5">Class</th>
                  <th className="font-normal px-2 py-1.5">Goods / Services</th>
                  <th className="font-normal px-2 py-1.5">User Date</th>
                  <th className="font-normal px-2 py-1.5">Place</th>
                </tr>
              </thead>
              <tbody>
                {data.similarMarks.map((sm, idx) => (
                  <tr
                    key={`${sm.tmNo ?? "row"}-${idx}`}
                    className="border-t border-[#EEEEEE] text-[#222222]"
                  >
                    <td className="px-2 py-1.5">{sm.mark ?? "—"}</td>
                    <td className="px-2 py-1.5">{sm.tmNo ?? "—"}</td>
                    <td className="px-2 py-1.5">{sm.class ?? "—"}</td>
                    <td className="px-2 py-1.5">{sm.goodsServices ?? "—"}</td>
                    <td className="px-2 py-1.5">{sm.userDate ?? "—"}</td>
                    <td className="px-2 py-1.5">{sm.place ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SubSection>
      )}
      {data.invoice && (
        <SubSection title="Invoice to Client (Reply to Objection)">
          {renderInvoice(data.invoice, "Reply to Objection Invoice PDF")}
        </SubSection>
      )}
      {data.confirmation && (
        <SubSection title="Confirmation from Client">
          {renderObjectionConfirmation(data.confirmation)}
        </SubSection>
      )}
      {data.replyFiled && (
        <SubSection title="Reply to Objection Filed">
          <FieldList
            fields={[
              {
                label: "Date of Reply to Objection Filed",
                value: data.replyFiled.dateOfReplyToObjectionFiled ?? "",
              },
            ]}
            files={[
              {
                label: "Reply to Objection",
                filename: data.replyFiled.replyToObjectionFilename,
                url: data.replyFiled.replyToObjectionFileUrl,
              },
            ]}
          />
        </SubSection>
      )}
    </div>
  );
}

function renderAccepted(data: StatusTimelineAcceptedData) {
  return (
    <FieldList
      fields={[]}
      files={[
        {
          label: "Examination Report Acceptance",
          filename: data.examinationReportAcceptanceFilename,
          url: data.examinationReportAcceptanceFileUrl,
        },
      ]}
    />
  );
}

function humanizeUpperSnake(value: string | null | undefined): string {
  if (!value) return "";
  return value
    .toString()
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function renderHearing(data: StatusTimelineHearingData) {
  const hasNotice = data.dateOfHearing || data.hearingNoticeFileUrl;
  return (
    <div className="flex flex-col gap-4">
      {hasNotice && (
        <FieldList
          fields={[{ label: "Date of Hearing", value: data.dateOfHearing ?? "" }]}
          files={[
            {
              label: "Hearing Notice",
              filename: data.hearingNoticeFilename,
              url: data.hearingNoticeFileUrl,
            },
          ]}
        />
      )}
      {data.invoice && (
        <SubSection title="Invoice to Client (Hearing)">
          {renderInvoice(data.invoice, "Hearing Invoice PDF")}
        </SubSection>
      )}
      {data.tmMBeforeHearingInvoice && (
        <SubSection title="Invoice for TM-M before Hearing">
          {renderInvoice(data.tmMBeforeHearingInvoice, "TM-M before Hearing Invoice PDF")}
        </SubSection>
      )}
      {data.tmMBeforeHearingFiled && (
        <SubSection title="File TM-M before Hearing">
          <FieldList
            fields={[
              {
                label: "Date of TM-M before Hearing Filed",
                value: data.tmMBeforeHearingFiled.dateOfTmMBeforeHearingFiled ?? "",
              },
            ]}
            files={[
              {
                label: "TM-M before Hearing",
                filename: data.tmMBeforeHearingFiled.tmMBeforeHearingFilename,
                url: data.tmMBeforeHearingFiled.tmMBeforeHearingFileUrl,
              },
            ]}
          />
        </SubSection>
      )}
      {data.noteOnHearing && (
        <SubSection title="Note on Hearing">
          <FieldList
            fields={[
              {
                label: "Note on Non-distinctiveness 1",
                value: data.noteOnHearing.noteNonDistinctive1 ?? "",
              },
              {
                label: "Note on Non-distinctiveness 2",
                value: data.noteOnHearing.noteNonDistinctive2 ?? "",
              },
              {
                label: "Supporting Docs Uploaded",
                value:
                  data.noteOnHearing.supportingDocsUploaded === true
                    ? "Yes"
                    : data.noteOnHearing.supportingDocsUploaded === false
                      ? "No"
                      : "",
              },
              {
                label: "Note on Other Objections 1",
                value: data.noteOnHearing.noteOtherObjections1 ?? "",
              },
              {
                label: "Note on Other Objections 2",
                value: data.noteOnHearing.noteOtherObjections2 ?? "",
              },
            ]}
            files={[
              {
                label: "Note on Hearing PDF",
                filename: data.noteOnHearing.notePdfKey
                  ? "Note on Hearing PDF"
                  : null,
                url: data.noteOnHearing.notePdfUrl,
              },
            ]}
          />
        </SubSection>
      )}
      {data.attendHearing && (
        <SubSection title="Attend Hearing">
          <FieldList
            fields={[
              {
                label: "Hearing Status",
                value: humanizeUpperSnake(data.attendHearing.hearingStatus),
              },
              {
                label: "Officer Remark",
                value: humanizeUpperSnake(data.attendHearing.hearingOfficerRemark),
              },
              {
                label: "Officer Directions",
                value:
                  data.attendHearing.officerDirections.length > 0
                    ? data.attendHearing.officerDirections
                        .map((d) => `${d.index}. ${d.direction}`)
                        .join("\n")
                    : "",
              },
              { label: "Remarks", value: data.attendHearing.remarks ?? "" },
            ]}
            files={[
              {
                label: "Postponement Notice",
                filename: data.attendHearing.postponementNoticeFilename,
                url: data.attendHearing.postponementNoticeFileUrl,
              },
            ]}
          />
        </SubSection>
      )}
      {data.orderOfHearing && (
        <SubSection title="Order of Hearing">
          <FieldList
            fields={[
              {
                label: "Outcome of Hearing",
                value: humanizeUpperSnake(data.orderOfHearing.outcomeOfHearing),
              },
            ]}
            files={[
              {
                label: "Order of Hearing",
                filename: data.orderOfHearing.orderOfHearingFilename,
                url: data.orderOfHearing.orderOfHearingFileUrl,
              },
            ]}
          />
        </SubSection>
      )}
    </div>
  );
}

function renderAdvertised(data: StatusTimelineAdvertisedData) {
  return (
    <FieldList
      fields={[
        { label: "Journal Date", value: data.journalDate ?? "" },
        {
          label: "Last Date for Opposition",
          value: data.lastDateForOpposition ?? "",
        },
      ]}
      files={[
        {
          label: "Order from Registry",
          filename: data.orderFromRegistryFilename,
          url: data.orderFromRegistryFileUrl,
        },
      ]}
    />
  );
}

function renderRegistered(data: StatusTimelineRegisteredData) {
  return (
    <FieldList
      fields={[
        { label: "Date of Issue", value: data.dateOfIssue ?? "" },
        { label: "Certificate No.", value: data.certificateNo ?? "" },
        { label: "Validity Date", value: data.validityDate ?? "" },
      ]}
      files={[
        {
          label: "Order of Registration",
          filename: data.orderOfRegistrationFilename,
          url: data.orderOfRegistrationFileUrl,
        },
      ]}
    />
  );
}

function renderRefusedAbandoned(data: StatusTimelineRefusedAbandonedData) {
  return (
    <FieldList
      fields={[
        {
          label: "Refused or Abandoned",
          value: data.refusalOrAbandonment ?? "",
        },
        { label: "Date of Order", value: data.dateOfOrder ?? "" },
        { label: "Reason", value: data.refusalReason ?? "" },
      ]}
      files={[
        {
          label: "Order of Refusal / Abandonment",
          filename: data.orderFilename,
          url: data.orderFileUrl,
        },
      ]}
    />
  );
}

function renderSegmentData(
  group: StatusTimelineGroup,
  data: StatusTimelineSegmentData,
) {
  if (!data) return null;
  switch (group) {
    case "formalities-check-fail":
      return renderFcFail(data as StatusTimelineFcFailData);
    case "examination-report-objected":
      return renderObjection(data as StatusTimelineObjectionData);
    case "examination-report-accepted":
    case "examination-report-accepted-after-objection":
      return renderAccepted(data as StatusTimelineAcceptedData);
    case "examination-report-hearing":
      return renderHearing(data as StatusTimelineHearingData);
    case "advertised":
    case "advertised-opposed":
    case "advertised-not-opposed":
      return renderAdvertised(data as StatusTimelineAdvertisedData);
    case "registered":
      return renderRegistered(data as StatusTimelineRegisteredData);
    case "refused":
    case "abandoned":
      return renderRefusedAbandoned(
        data as StatusTimelineRefusedAbandonedData,
      );
    default:
      return null;
  }
}

function SegmentLoadingPlaceholder() {
  return (
    <div
      className="flex items-center gap-2 text-xs text-[#6D6D6D]"
      style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
    >
      <span className="inline-block w-3 h-3 rounded-full border-2 border-[#EEEEEE] border-t-[#6D6D6D] animate-spin" />
      Loading details…
    </div>
  );
}

function SegmentErrorPlaceholder({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div
      className="flex flex-col gap-1.5 text-xs"
      style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
    >
      <span className="text-[#B91C1C]">{message}</span>
      <button
        type="button"
        onClick={onRetry}
        className="self-start px-2 py-1 rounded-[6px] border border-[#EEEEEE] bg-white text-[#222222] hover:bg-[#F7F7F7] transition-colors"
      >
        Retry
      </button>
    </div>
  );
}

function renderSegmentBody({
  group,
  actions,
  segmentState,
  onRetry,
}: {
  group: StatusTimelineGroup;
  actions: TimelineAction[];
  segmentState: SegmentDataState | undefined;
  onRetry: () => void;
}) {
  const hasActions = actions && actions.length > 0;
  const dataCapable = isDataGroup(group);
  const dataNode =
    dataCapable && segmentState?.loaded
      ? renderSegmentData(group, segmentState.data)
      : null;
  const showDataLoading = dataCapable && segmentState?.loading;
  const showDataError = dataCapable && !!segmentState?.error;

  if (!dataNode && !showDataLoading && !showDataError && !hasActions) {
    return (
      <p
        className="text-xs text-[#6D6D6D]"
        style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
      >
        No details or activity captured for this status.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {showDataLoading && <SegmentLoadingPlaceholder />}
      {showDataError && (
        <SegmentErrorPlaceholder
          message={segmentState?.error || "Failed to load details"}
          onRetry={onRetry}
        />
      )}
      {dataNode}
      {hasActions && <ActivityList actions={actions} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Timeline node
// ---------------------------------------------------------------------------

function TimelineNode({
  segment,
  isFirst,
  isLast,
  defaultOpen,
  segmentState,
  onEnsureData,
}: {
  segment: StatusTimelineSegment;
  isFirst: boolean;
  isLast: boolean;
  defaultOpen: boolean;
  segmentState: SegmentDataState | undefined;
  onEnsureData: (group: StatusTimelineGroup) => void;
}) {
  const hasActivity = (segment.actions?.length ?? 0) > 0;
  const [open, setOpen] = useState(defaultOpen);
  const dotColor = segment.isCurrent ? "#222222" : "#6D6D6D";
  const dotRing = segment.isCurrent ? "#22222220" : "transparent";

  // When the card is open and this is a data-capable group, ensure the
  // segment's stage data has been (or is being) fetched.
  const dataCapable = isDataGroup(segment.group);
  const needsFetch =
    dataCapable && !segmentState?.loaded && !segmentState?.loading && !segmentState?.error;
  useEffect(() => {
    if (open && needsFetch) {
      onEnsureData(segment.group);
    }
  }, [open, needsFetch, onEnsureData, segment.group]);

  const handleRetry = useCallback(() => {
    onEnsureData(segment.group);
  }, [onEnsureData, segment.group]);

  return (
    <div className="flex gap-3">
      {/* Rail (dot + connector) */}
      <div className="flex flex-col items-center pt-1">
        <div
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{
            backgroundColor: dotColor,
            boxShadow: `0 0 0 3px ${dotRing}`,
          }}
        />
        {!isLast && (
          <div
            className="w-px flex-1 mt-1"
            style={{ backgroundColor: "#EEEEEE", minHeight: "24px" }}
          />
        )}
      </div>

      {/* Card */}
      <div
        className={`flex-1 rounded-xl border border-[#EEEEEE] bg-white ${
          isFirst ? "" : ""
        } ${isLast ? "mb-0" : "mb-3"}`}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-start justify-between gap-3 px-4 py-3 text-left hover:bg-[#FCFCFC] transition-colors rounded-xl"
        >
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="text-sm font-medium text-[#222222]"
                style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
              >
                {segment.status}
              </span>
              {segment.isCurrent && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#222222] text-white"
                  style={{ lineHeight: "16px" }}
                >
                  Current
                </span>
              )}
              {hasActivity && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#F7F7F7] text-[#222222] border border-[#EEEEEE]"
                  style={{ lineHeight: "16px" }}
                >
                  {segment.actions.length}{" "}
                  {segment.actions.length === 1 ? "activity" : "activities"}
                </span>
              )}
            </div>
            <div
              className="flex flex-row flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-[#6D6D6D]"
              style={{ fontFamily: "Inter, sans-serif", lineHeight: "16px" }}
            >
              {segment.enteredAt && (
                <span>Entered: {segment.enteredAt}</span>
              )}
              {segment.exitedAt && <span>Exited: {segment.exitedAt}</span>}
              {segment.changedBy?.name && (
                <span>Changed by: {segment.changedBy.name}</span>
              )}
            </div>
          </div>
          <ChevronIcon open={open} />
        </button>

        {open && (
          <div className="px-4 pb-4 pt-1 border-t border-[#EEEEEE]">
            {renderSegmentBody({
              group: segment.group,
              actions: segment.actions,
              segmentState,
              onRetry: handleRetry,
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function StatusHistoryView({ leadId }: StatusHistoryViewProps) {
  const [tmEntries, setTmEntries] = useState<TmEntry[]>([]);
  const [selectedTmIdx, setSelectedTmIdx] = useState(0);
  const [tmListLoading, setTmListLoading] = useState(!!leadId);
  const [timeline, setTimeline] = useState<StatusTimelineResponse | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Per-group cache for lazy-loaded segment data. Reset whenever the selected
  // lead/TM changes so we never show stale rows from a previous TM.
  const [segmentDataByGroup, setSegmentDataByGroup] = useState<
    Record<string, SegmentDataState>
  >({});
  // Bumped on every TM/lead change so stale in-flight responses are ignored.
  const fetchTokenRef = useRef(0);
  // Tracks groups currently being fetched so a re-render mid-fetch can't
  // double-fire a request before React commits the loading state.
  const inFlightRef = useRef<Set<string>>(new Set());

  // Load TM entries for the lead (status omitted → backend returns all apps).
  useEffect(() => {
    if (!leadId) {
      setTmEntries([]);
      setTmListLoading(false);
      return;
    }
    let cancelled = false;
    setTmListLoading(true);
    getApplicationsByStatus("", "NEW_APP", leadId)
      .then((res) => {
        if (cancelled) return;
        setTmEntries(
          (res.applications ?? []).map((a: ApplicationsByStatusItem) => ({
            tmNo: a.tmNo,
            mark: a.mark,
          })),
        );
        setSelectedTmIdx(0);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load applications",
          );
          setTmEntries([]);
        }
      })
      .finally(() => {
        if (!cancelled) setTmListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Load the lightweight timeline (segments + actions only — no per-stage data).
  // Per-segment data is fetched on demand when the user expands a segment.
  const selectedTm = tmEntries[selectedTmIdx]?.tmNo;
  useEffect(() => {
    // Reset the per-segment cache and invalidate any in-flight segment fetches
    // before kicking off the timeline reload.
    fetchTokenRef.current += 1;
    inFlightRef.current.clear();
    setSegmentDataByGroup({});

    if (!leadId || !selectedTm) {
      setTimeline(null);
      return;
    }
    let cancelled = false;
    setTimelineLoading(true);
    getStatusTimeline(leadId, selectedTm, { light: true })
      .then((res) => {
        if (!cancelled) setTimeline(res);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load status history",
          );
          setTimeline(null);
        }
      })
      .finally(() => {
        if (!cancelled) setTimelineLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selectedTm]);

  const ensureSegmentData = useCallback(
    (group: StatusTimelineGroup) => {
      if (!leadId || !selectedTm) return;
      if (!isDataGroup(group)) return;
      if (inFlightRef.current.has(group)) return;
      inFlightRef.current.add(group);

      setSegmentDataByGroup((prev) => ({
        ...prev,
        [group]: { loading: true, loaded: false, data: null, error: null },
      }));

      const myToken = fetchTokenRef.current;
      getStatusTimelineSegment(leadId, selectedTm, group)
        .then((res) => {
          inFlightRef.current.delete(group);
          if (myToken !== fetchTokenRef.current) return;
          setSegmentDataByGroup((prev) => ({
            ...prev,
            [group]: {
              loading: false,
              loaded: true,
              data: res.data,
              error: null,
            },
          }));
        })
        .catch((err) => {
          inFlightRef.current.delete(group);
          if (myToken !== fetchTokenRef.current) return;
          const msg =
            err instanceof Error ? err.message : "Failed to load segment";
          setSegmentDataByGroup((prev) => ({
            ...prev,
            [group]: { loading: false, loaded: false, data: null, error: msg },
          }));
        });
    },
    [leadId, selectedTm],
  );

  // Display timeline newest first (most recent change at top).
  const orderedSegments = useMemo(() => {
    if (!timeline?.timeline?.length) return [];
    return [...timeline.timeline].reverse();
  }, [timeline]);

  // Auto-open the latest data-capable segment OR any segment with activity so
  // the user lands on something useful without having to click. Other segments
  // start collapsed; expanding them triggers their lazy data fetch.
  const defaultOpenIndex = useMemo(() => {
    if (!orderedSegments.length) return -1;
    const idx = orderedSegments.findIndex(
      (s) => isDataGroup(s.group) || (s.actions?.length ?? 0) > 0,
    );
    return idx >= 0 ? idx : 0;
  }, [orderedSegments]);

  if (tmListLoading) {
    return (
      <div
        className="flex flex-col gap-5 w-full max-w-[1085px]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  if (!leadId || tmEntries.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-1 border border-dashed border-[#EEEEEE] rounded-xl py-12 px-6 text-center w-full max-w-[1085px]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm font-medium text-[#222222]">
          No applications to show.
        </p>
        <p className="text-xs text-[#6D6D6D]">
          Once acknowledgement is submitted, application history will appear here.
        </p>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* TM No. selector */}
      <div className="flex flex-row flex-wrap gap-3">
        {tmEntries.map((entry, idx) => {
          const isActive = selectedTmIdx === idx;
          return (
            <button
              key={entry.tmNo}
              type="button"
              onClick={() => setSelectedTmIdx(idx)}
              className={`flex items-center gap-2.5 px-2.5 py-2 rounded-[6px] h-10 min-w-[140px] border transition-colors ${
                isActive
                  ? "bg-[#222222] text-white border-transparent"
                  : "bg-[#FCFCFC] text-[#6D6D6D] border-[#EEEEEE]"
              }`}
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: "12px",
                fontWeight: isActive ? 500 : 400,
                lineHeight: "20px",
              }}
            >
              <RadioCircle active={isActive} />
              {formatTmNoForDisplay(entry.tmNo)}
            </button>
          );
        })}
      </div>

      {/* Application summary */}
      {timelineLoading && !timeline ? (
        <p className="text-sm text-[#6D6D6D]">Loading history…</p>
      ) : timeline ? (
        <>
          <ApplicationSummary
            app={timeline.application}
            acknowledgementNo={timeline.acknowledgementNo}
            dateOfFiling={timeline.dateOfFiling}
          />

          <div className="flex items-center justify-between">
            <span
              className="text-sm font-medium text-[#222222]"
              style={{ lineHeight: "20px" }}
            >
              Status Journey
            </span>
            <span
              className="text-[11px] text-[#6D6D6D]"
              style={{ lineHeight: "16px" }}
            >
              {orderedSegments.length}{" "}
              {orderedSegments.length === 1 ? "entry" : "entries"}
            </span>
          </div>

          {orderedSegments.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center gap-1 border border-dashed border-[#EEEEEE] rounded-xl py-12 px-6 text-center"
            >
              <p className="text-sm font-medium text-[#222222]">
                No status changes recorded yet.
              </p>
              <p className="text-xs text-[#6D6D6D]">
                Once status changes are made, they will appear here.
              </p>
            </div>
          ) : (
            <div className="flex flex-col">
              {orderedSegments.map((segment, idx) => (
                <TimelineNode
                  key={`${segment.status}-${segment.enteredAt ?? "initial"}-${idx}`}
                  segment={segment}
                  isFirst={idx === 0}
                  isLast={idx === orderedSegments.length - 1}
                  defaultOpen={idx === defaultOpenIndex}
                  segmentState={segmentDataByGroup[segment.group]}
                  onEnsureData={ensureSegmentData}
                />
              ))}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
